import { Controller } from '../utils/controller';
import { isMobile } from '../utils/ismobile';
import debounce from 'lodash/debounce';

interface ItemType {
  itemLabel: string;
  menuItemIndex: number;
  hasSubmenu: boolean;
}

export default class extends Controller {
  declare readonly navPanelTarget: HTMLElement;
  static requiredOutlets = [];

  static targets = [
    'bgMask',
    'childlessNavLink',
    'mobileNavToggle',
    'navbarOverlay',
    'navList',
    'navPanel',
    'parentNav',
    'panelLogo',
    'sideExpansionInnerPanel',
    'sideExpansionPanel',
    'sideMainPanel',
    'sideMainPanelLink',
    'sideNav',
    'sidePanel',
    'sidePanelExit',
    'subnav',
    'subNavHeader',
    'subNavList',
    'subNavGroup'
  ];
  static requiredTargets = [];

  static values = {
    subnavhidetimeout: Number,
    scrolltoggleelements: String,
    scrolltogglethreshold: Number
  };

  static classes = [
    'hidden',
    'scrollDown',
    'scrollUp',
    'active',
    'subNavActive',
    'subNavInactive',
    'sidePanelWidth',
    'sidePanelExpandedWidth',
    'sidePanelExpandedActive',
    'sidePanelActiveLink',
    'sideMainPanelExpanded',
    'moveHidden',
    'overflowHidden',
    'invisible',
    'dimBg',
    'opacity',
    'bringToFront',
    'mobileNavActive',
    'inactiveParent',
    'subSubNavActive',
    'footerSubnavActive'
  ];
  static requiredClasses = [];

  // TARGETS
  declare bgMaskTarget: HTMLDivElement;
  declare childlessNavLinkTargets: HTMLLIElement[];
  declare mobileNavToggleTarget: HTMLHeadElement;
  declare navbarOverlayTarget: HTMLDivElement;
  declare panelLogoTarget: HTMLDivElement;
  declare parentNavTargets: HTMLElement[];
  declare readonly hasMobileNavToggleTarget: boolean;
  declare readonly hasNavbarOverlayTarget: boolean;
  declare readonly hasSideExpansionPanelTarget: boolean;
  declare readonly hasSideExpansionInnerPanelTargets: boolean;
  declare sideExpansionInnerPanelTargets: HTMLDivElement[];
  declare sideExpansionPanelTarget: HTMLDivElement;
  declare sideMainPanelLinkTargets: HTMLDivElement[];
  declare sideMainPanelTarget: HTMLDivElement;
  declare sideNavTarget: HTMLDivElement;
  declare sidePanelExitTarget: SVGElement;
  declare sidePanelTarget: HTMLDivElement;
  declare subNavListTargets: HTMLUListElement[];
  declare subnavTargets: HTMLDivElement[];
  declare subNavGroupTargets: HTMLUListElement[];

  // CLASSES
  declare activeClasses: string[];
  declare bringToFrontClass: string;
  declare dimBgClass: string;
  declare footerSubnavActiveClasses: string[];
  declare hiddenClasses: string[];
  declare inactiveParentClasses: string[];
  declare invisibleClasses: string[];
  declare mobileNavActiveClass: string;
  declare moveHiddenClasses: string[];
  declare opacityClass: string;
  declare overflowHiddenClass: string;
  declare scrollDownClasses: string[];
  declare scrollUpClasses: string[];
  declare sideMainPanelExpandedClass: string;
  declare sidePanelActiveLinkClass: string;
  declare sidePanelExpandedActiveClass: string;
  declare sidePanelExpandedWidthClass: string;
  declare sidePanelWidthClass: string;
  declare subNavActiveClasses: string[];
  declare subNavInactiveClasses: string[];
  declare subSubNavActiveClasses: string[];

  // VALUES
  declare subnavhidetimeoutValue: number | undefined;
  declare scrolltoggleelementsValue: string;
  declare scrolltogglethresholdValue: number;

  // LOCAL STATE
  private currNavIndex: number;
  private currSubNavIndex: number;
  private lastTimeout: number | undefined;
  private lastScroll: number;

  connect() {
    this.currNavIndex = -1;
    this.currSubNavIndex = -1;
    this.lastTimeout = undefined;
    this.toggleScrollListener(true);
  }

  disconnect() {
    this.toggleScrollListener(false);
  }

  toggleScrollListener(isAttach) {
    const isValidBehavior =
      this.hasMobileNavToggleTarget && (this.scrollDownClasses || this.scrollUpClasses);
    if (!isMobile() || !isValidBehavior) {
      return;
    }
    const handleMobileScroll = debounce(() => {
      this.handleScroll();
    }, 10).bind(this);
    if (isAttach) {
      document.addEventListener('scroll', handleMobileScroll);
    } else {
      document.removeEventListener('scroll', handleMobileScroll);
    }
  }

  handleScroll() {
    const resetPanels = (isMobileNavHiding) => {
      if (isMobileNavHiding && !this.isMobileNavOpen()) {
        this.toggleMobileNav();
      }
      document.querySelectorAll(this.scrolltoggleelementsValue).forEach((elem) => {
        elem.setAttribute('style', isMobileNavHiding ? 'top: 0' : '');
      });
    };
    const goingUp = () => {
      this.mobileNavToggleTarget.classList.remove(...this.scrollDownClasses);
      this.mobileNavToggleTarget.classList.add(...this.scrollUpClasses);
      resetPanels();
    };
    const goingDown = () => {
      this.mobileNavToggleTarget.classList.remove(...this.scrollUpClasses);
      this.mobileNavToggleTarget.classList.add(...this.scrollDownClasses);
      resetPanels(true);
    };
    if (
      window.scrollY <
        window.innerHeight *
          (this.scrolltogglethresholdValue ? this.scrolltogglethresholdValue : 0.35) ||
      window.scrollY >= document.body.scrollHeight - window.innerHeight * 2
    ) {
      goingUp();
    } else {
      if (window.scrollY > this.lastScroll) {
        goingDown();
      } else if (window.scrollY < this.lastScroll) {
        goingUp();
      }
    }
    this.lastScroll = window.scrollY;
  }

  handleMouseOver(event) {
    if (isMobile()) {
      return false;
    }
    clearTimeout(this.lastTimeout);
    this.showSubNav(event.target);
    this.parentNavTargets.forEach((nav) => {
      if (nav.dataset.parentnavIndex != String(this.currNavIndex)) {
        nav.classList.add(...this.inactiveParentClasses);
      }
    });
  }

  handleNavClick(event) {
    const _handleNavClickOpen = (target) => {
      target.setAttribute('aria-expanded', 'true');
      this.handleMouseOver(event);
    };

    const _handleNavClickClose = (target) => {
      target.setAttribute('aria-expanded', 'false');
      this.handleMouseLeave(event);
    };

    const addCloseNavListener = (menuGroup, target) => {
      // When ESC / TAB are used in a menu, handle the interaction
      menuGroup.querySelectorAll('[role="menu"]').forEach((menu) => {
        menu.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.code === 'Escape') {
            _handleNavClickClose(target);
            target.focus();
          }
        });
      });
    };

    // This represents a keyboard / screen reader which should be able to open the navigation bar.
    if (event.screenX === 0 && event.screenY === 0) {
      event.preventDefault();
    }

    this.triggerNavClickEvent(event);

    if (!isMobile()) {
      if (event.screenX === 0 && event.screenY === 0) {
        const target = event.target as HTMLElement;
        const isExpandedAttributeValue = target.getAttribute('aria-expanded');
        if (!isExpandedAttributeValue || isExpandedAttributeValue === 'false') {
          const menuGroup = document.getElementById(target.getAttribute('aria-controls') as string);
          if (menuGroup != null) {
            // Open subnav, focus, and wait for close
            _handleNavClickOpen(target);
            (menuGroup.querySelector('a, button') as HTMLElement).focus();
            addCloseNavListener(menuGroup, target);
          }
        } else {
          _handleNavClickClose(target);
        }
      }
      return false;
    }
    if (this.currNavIndex == event.currentTarget.dataset.parentnavIndex) {
      this.hideSubNav(event.currentTarget);
    } else {
      this.showSubNav(event.currentTarget);
      if (event.currentTarget.dataset.parentnavIndex) {
        this.parentNavTargets[
          event.currentTarget.dataset.parentnavIndex
        ]?.firstElementChild?.classList.add(...this.subNavActiveClasses);
      }

      // When the inactiveParent class is set, apply the class to top level
      // nav items (childlessNavLinks/parentNavs) that were not clicked
      this.parentNavTargets.forEach((nav) => {
        if (nav.dataset.parentnavIndex != String(this.currNavIndex)) {
          nav.classList.add(...this.inactiveParentClasses);
        }
      });

      this.childlessNavLinkTargets.forEach((nav) => {
        nav.classList.add(...this.inactiveParentClasses);
      });
    }

    // prevent navigation to parent pages when expanding nav
    if (event.params?.preventDefault === 'mobile' && isMobile()) {
      event.preventDefault();
    }
  }

  handleSubNavClick(event) {
    this.triggerSubNavClickEvent(event);

    event.stopPropagation();
    if (!isMobile()) {
      return false;
    }
    this.showSubSubNav(event.currentTarget);
  }

  triggerNavClickEvent(event) {
    const navClickEvent = new CustomEvent('nav.globalNavClick', {
      detail: { event }
    });
    window.dispatchEvent(navClickEvent);
  }

  triggerSubNavClickEvent(event) {
    const subNavClickEvent = new CustomEvent('nav.subNavClick', {
      detail: { event }
    });
    window.dispatchEvent(subNavClickEvent);
  }

  handleSubNavToggle(event) {
    if (!isMobile()) {
      return false;
    }
    const container = event.currentTarget.parentElement;
    const expandIcon = container.querySelector('[data-icon="expand"]');
    const collapseIcon = container.querySelector('[data-icon="collapse"]');
    const subNavList = container.querySelector('[data-nav-target="subNavList"]');
    expandIcon?.classList.toggle('hidden');
    collapseIcon?.classList.toggle('hidden');
    this.hiddenClasses.forEach((className) => subNavList?.classList.toggle(className));
  }

  handleFooterNavToggle(event) {
    if (!isMobile()) {
      return false;
    }
    if (event.target.getAttribute('target') === '_blank') {
      event.stopPropagation();
      return;
    }

    const container = event.currentTarget;

    const isActive = this.footerSubnavActiveClasses.some((className) =>
      container.classList.contains(className)
    );
    const footer = container.querySelector('[data-nav-target="footerNavList"]');

    if (isActive === true) {
      container.classList.remove(...this.footerSubnavActiveClasses);
      footer?.classList.add(...this.hiddenClasses);
    } else {
      container.classList.add(...this.footerSubnavActiveClasses);
      footer?.classList.remove(...this.hiddenClasses);
    }
  }

  showMobileNav() {
    this.navPanelTarget.classList.remove(...this.hiddenClasses);
    this.bgMaskTarget.classList.remove(...this.hiddenClasses);

    const target = document.querySelector('[data-behavior="search-panel"]');
    if (target) {
      this.dispatch('hideSearch', { target });
    }

    const body = document.body;
    body.classList.add('overflow-hidden');
  }

  handleMobileNavClick(e) {
    e.preventDefault();
    if (!this.hasMobileNavToggleTarget) {
      return;
    }
    const isOpen = this.isMobileNavOpen();
    this.toggleMobileNav(!isOpen);
  }

  openItem(event) {
    const target = event.params.item;
    const menuItem = this.parentNavTargets.find((el, current) => current == target);
    const subMenu = this.subnavTargets.find((el, current) => current == target);

    if (menuItem) {
      menuItem.classList.add(...this.activeClasses);
    }

    if (subMenu) {
      subMenu.classList.remove(...this.subNavInactiveClasses);
      subMenu.classList.add(...this.subNavActiveClasses);
    }
  }

  closeItem(event) {
    const target = event.params.item;
    const menuItem = this.parentNavTargets.find((el, current) => current == target);
    const subMenu = this.subnavTargets.find((el, current) => current == target);

    if (menuItem) {
      menuItem.classList.remove(...this.activeClasses);
    }

    if (subMenu) {
      subMenu.classList.add(...this.subNavInactiveClasses);
      subMenu.classList.remove(...this.subNavActiveClasses);
    }
  }

  toggleMobileNav(isOpen = false) {
    if (!this.hasMobileNavToggleTarget) {
      return;
    }

    if (isOpen) {
      const target = document.querySelector('[data-behavior="search-panel"]');
      if (target) {
        this.dispatch('hideSearch', { target });
      }
    }

    this.mobileNavToggleTarget.classList.toggle(this.mobileNavActiveClass, isOpen);
    document.body.classList.toggle(this.overflowHiddenClass, isOpen);
  }

  isMobileNavOpen() {
    return (
      this.hasMobileNavToggleTarget &&
      this.mobileNavToggleTarget.classList.contains(this.mobileNavActiveClass)
    );
  }

  handleSideNavClick() {
    this.sideNavTarget.classList.remove(...this.invisibleClasses);
    this.sideNavTarget.classList.add(this.dimBgClass);
    this.sidePanelTarget.classList.add(...this.moveHiddenClasses);
    this.panelLogoTarget.classList.remove(this.opacityClass);
    document.body.classList.add(this.overflowHiddenClass);
  }

  handleSidePanelItemClick(event) {
    this.sidePanelTarget.classList.replace(
      this.sidePanelWidthClass,
      this.sidePanelExpandedWidthClass
    );
    this.sideExpansionPanelTarget.classList.remove(...this.invisibleClasses);

    const selectedIndex = Number(event.params.index);

    for (let i = 0; i < this.sideExpansionInnerPanelTargets.length; i++) {
      if (i == selectedIndex) {
        this.sideExpansionInnerPanelTargets[i].classList.remove(this.opacityClass);
        this.sideExpansionInnerPanelTargets[i].classList.add(this.bringToFrontClass);
        continue;
      }
      this.sideExpansionInnerPanelTargets[i].classList.remove(this.bringToFrontClass);
      this.sideExpansionInnerPanelTargets[i].classList.add(this.opacityClass);
    }

    const inActiveLinkIndexes = [...Array(this.sideMainPanelLinkTargets.length).keys()];
    inActiveLinkIndexes.splice(selectedIndex, 1);
    inActiveLinkIndexes.forEach((i) =>
      this.sideMainPanelLinkTargets[i].classList.remove(this.sidePanelActiveLinkClass)
    );
    this.sideMainPanelLinkTargets[selectedIndex].classList.add(this.sidePanelActiveLinkClass);
    this.sideMainPanelTarget.classList.add(this.sidePanelExpandedActiveClass);

    this.sideMainPanelTarget.classList.add(this.sideMainPanelExpandedClass);
  }

  closeSideExpansionPanel(event) {
    const index = Number(event.params.index);
    this.sideExpansionInnerPanelTargets[index].classList.add(this.opacityClass);
    this.sideExpansionPanelTarget.classList.add(...this.invisibleClasses);
  }

  hideSideNav(event) {
    if (event.target != this.sideNavTarget && event.target != this.sidePanelExitTarget) {
      return;
    }
    this.panelLogoTarget.classList.add(this.opacityClass);
    this.sidePanelTarget.classList.remove(...this.moveHiddenClasses);
    this.sideNavTarget.classList.remove(this.dimBgClass);
    this.sideNavTarget.classList.add(...this.invisibleClasses);
    this.sidePanelTarget.classList.replace(
      this.sidePanelExpandedWidthClass,
      this.sidePanelWidthClass
    );
    if (this.hasSideExpansionPanelTarget) {
      this.sideExpansionPanelTarget.classList.add(...this.invisibleClasses);
    }
    if (this.hasSideExpansionInnerPanelTargets) {
      this.sideExpansionInnerPanelTargets.forEach((panel) => {
        panel.classList.add(this.opacityClass);
        panel.classList.remove(this.bringToFrontClass);
      });
    }
    this.sideMainPanelTarget.classList.remove(this.sidePanelExpandedActiveClass);
    this.sideMainPanelTarget.classList.remove(this.sideMainPanelExpandedClass);
    this.sideMainPanelLinkTargets.forEach((link) =>
      link.classList.remove(this.sidePanelActiveLinkClass)
    );
    document.body.classList.remove(this.overflowHiddenClass);
  }

  handleCloseIconSubnav(event) {
    this.hideSubNavOverlay(event);
  }

  hideMobileNav() {
    this.navPanelTarget.classList.add(...this.hiddenClasses);
    this.bgMaskTarget.classList.add(...this.hiddenClasses);

    const body = document.body;
    body.classList.remove('overflow-hidden');
  }

  showSubSubNav(parentEle) {
    const nextSubNavIndex = parentEle.dataset.subNavIndex;
    if (this.currSubNavIndex == nextSubNavIndex) {
      return null;
    }
    this.currSubNavIndex = nextSubNavIndex;
    const subSubNavs = this.subNavListTargets;

    subSubNavs.forEach((nav) => {
      if (nav.dataset.subNavIndex == String(this.currSubNavIndex)) {
        nav.classList.remove(...this.hiddenClasses);
        (nav.parentNode as HTMLElement).classList.add(...this.subSubNavActiveClasses);
      } else {
        nav.classList.add(...this.hiddenClasses);
        (nav.parentNode as HTMLElement).classList.remove(...this.subSubNavActiveClasses);
      }
    });
  }

  showSubNav(parentEle) {
    const lastNavIndex = this.currNavIndex;
    const parentNavIndex = parentEle.dataset.parentnavIndex;
    this.currNavIndex = parentNavIndex;
    if (lastNavIndex == this.currNavIndex) {
      return null;
    }
    this.handleResetSubSubNavs();

    if (this.hasNavbarOverlayTarget) {
      this.navbarOverlayTarget.classList.remove(...this.hiddenClasses);
    }

    this.parentNavTargets.forEach((nav) => {
      nav.classList.remove(...this.inactiveParentClasses);
      if (nav.dataset.parentnavIndex == String(this.currNavIndex)) {
        nav.classList.add(...this.activeClasses);
      } else {
        nav.classList.remove(...this.activeClasses);
        nav.firstElementChild?.classList.remove(...this.subNavActiveClasses);
      }
    });
    this.childlessNavLinkTargets.forEach((nav) => {
      nav.classList.remove(...this.inactiveParentClasses);
    });

    this.subnavTargets.forEach((target) => {
      if (target.dataset.parentnavIndex == String(lastNavIndex)) {
        target.classList.add(...this.hiddenClasses);
      } else if (target.dataset.parentnavIndex == parentNavIndex) {
        target.classList.remove(...this.hiddenClasses);
      }
    });
  }

  showMenuMask({ params }: { params: Pick<ItemType, 'hasSubmenu'> }) {
    this.element.dispatchEvent(
      new CustomEvent('menuMaskHidden', {
        detail: { params },
        bubbles: false
      })
    );
  }

  handleResetSubSubNavs() {
    if (isMobile()) {
      this.resetSubSubNavOnMobile();
    } else {
      this.resetSubSubNavOnDesktop();
    }
  }

  // On desktop, always expand all sub-subnavs
  resetSubSubNavOnDesktop() {
    this.subNavListTargets.forEach((nav) => {
      nav.classList.remove(...this.hiddenClasses);
    });
  }

  // On mobile, expand first sub-subnavs, only
  resetSubSubNavOnMobile() {
    this.element.dispatchEvent(new CustomEvent('menuMaskHidden'));
    // On Mobile, expand first-child subnav group and collapse all others
    this.subNavGroupTargets?.forEach((subNavGroup) => {
      // Collapse all non-first-child elements and display expand icons
      subNavGroup
        .querySelectorAll('div:not(:first-child) [data-icon="expand"]')
        .forEach((icon) => icon.classList.remove('hidden'));
      subNavGroup
        .querySelectorAll('div:not(:first-child) [data-icon="collapse"]')
        .forEach((icon) => icon.classList.add('hidden'));
      subNavGroup.querySelectorAll('div:not(:first-child) ul')?.forEach((subNav) => {
        subNav.classList.add(...this.hiddenClasses);
      });
      subNavGroup.querySelectorAll('div:not(:first-child) div')?.forEach((subNav) => {
        subNav
          .querySelector('div:first-child div')
          ?.classList.remove(...this.subSubNavActiveClasses);
      });
      // Expand all first-child elements and display collapse icons
      subNavGroup.querySelector('div:first-child [data-icon="expand"]')?.classList.add('hidden');
      subNavGroup
        .querySelector('div:first-child [data-icon="collapse"]')
        ?.classList.remove('hidden');
      subNavGroup
        .querySelector('div:first-child div')
        ?.classList.add(...this.subSubNavActiveClasses);
      subNavGroup.querySelector('div:first-child ul')?.classList.remove(...this.hiddenClasses);
    });
  }

  handleMouseLeave(event) {
    if (isMobile()) {
      return false;
    }

    if (this.hasNavbarOverlayTarget) {
      this.navbarOverlayTarget.classList.add(...this.hiddenClasses);
    }

    this.lastTimeout = setTimeout(() => {
      this.hideSubNav(event.target);
      this.element.dispatchEvent(new CustomEvent('menuMaskHidden'));
    }, this.subnavhidetimeoutValue);
  }

  hideSubNavOverlay(event) {
    if (isMobile()) {
      return false;
    }
    this.hideSubNav(event.target);
    this.element.dispatchEvent(new CustomEvent('menuMaskHidden'));
  }

  hideSubNav(parentEle) {
    this.currNavIndex = -1;
    const targets = this.subnavTargets;

    targets.forEach((target) => {
      target.classList.add(...this.hiddenClasses);
    });

    this.parentNavTargets
      .find((ele) => ele.dataset.parentnavIndex == parentEle.dataset.parentnavIndex)
      ?.classList.remove(...this.activeClasses);

    this.parentNavTargets.forEach((parent) => {
      parent.firstElementChild?.classList.remove(...this.subNavActiveClasses);
      parent.classList.remove(...this.inactiveParentClasses);
    });

    this.childlessNavLinkTargets.forEach((nav) => {
      nav.classList.remove(...this.inactiveParentClasses);
    });
  }

  handleMouseEnterSearch() {
    const elem = document.querySelector('[data-behavior="search-panel"]');
    if (elem) {
      this.dispatch('showSearch', {
        target: elem
      });
    }
  }

  handleMouseLeaveSearch() {
    const elem = document.querySelector('[data-behavior="search-panel"]');
    if (elem) {
      this.dispatch('leaveSearch', {
        target: elem
      });
    }
  }

  handleClickSearch(event) {
    event.preventDefault();
    this.toggleMobileNav(false);

    const elem = document.querySelector('[data-behavior="search-panel"]');
    if (elem) {
      this.dispatch('toggleSearch', {
        target: elem
      });
    }
  }

  handleSearchInput(event) {
    if (event.target.value.length > 0) {
      const elem = document.querySelector('[data-behavior="search-panel"]');
      if (elem) {
        this.dispatch('showSearch', {
          target: elem
        });
      }
    } else if (event.target.value.length == 0) {
      const elem = document.querySelector('[data-behavior="search-panel"]');
      if (elem) {
        this.dispatch('leaveSearch', {
          target: elem
        });
      }
    }
    const elem = document.querySelector('#predictive-search-wrapper');
    if (elem) {
      this.dispatch('handleSearchInput', {
        target: elem,
        detail: {
          value: event.target.value
        }
      });
    }
  }
}
